/**
 * Google Sheets API 클라이언트
 *
 * Google Sheets API v4를 사용하여 스프레드시트 데이터를 읽고 씁니다.
 */

import { env } from '@/core/config/env'
import { STORAGE_KEYS } from '@/core/config/constants'
import { logger } from '@/core/logger'

/**
 * Google Sheets API 커스텀 에러 클래스들
 */
export class SheetsAuthenticationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SheetsAuthenticationError'
  }
}

export class SheetsPermissionError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SheetsPermissionError'
  }
}

export class SheetsNotFoundError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SheetsNotFoundError'
  }
}

export class SheetsRateLimitError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SheetsRateLimitError'
  }
}

/**
 * 정해진 시간 안에 응답이 오지 않아 요청을 끊은 경우
 *
 * @why fetch는 기본 타임아웃이 없다. 모바일에서 절전·네트워크 전환 후
 * 재사용된 죽은 커넥션으로 요청이 나가면 응답이 영영 오지 않고,
 * 그 promise를 await하던 화면은 스피너가 멈추지 않는 상태로 굳는다.
 * "느리다"가 아니라 "끝나지 않는다"가 실제 장애였으므로 별도 타입으로 구분한다.
 */
export class SheetsTimeoutError extends Error {
  constructor(
    message: string,
    /** 끊기까지 기다린 시간(ms) */
    readonly timeoutMs: number,
    /**
     * 요청이 서버에 도달했을 가능성이 있는지 여부
     *
     * @why 타임아웃은 "요청이 안 갔다"가 아니라 "응답을 못 받았다"이다.
     * 쓰기 요청이면 서버에는 이미 반영됐을 수 있으므로, 호출측이
     * 무턱대고 재시도하지 않도록 이 사실을 함께 전달한다.
     */
    readonly mayHaveApplied: boolean
  ) {
    super(message)
    this.name = 'SheetsTimeoutError'
  }
}

/**
 * 네트워크 자체에 도달하지 못한 경우 (오프라인, DNS 실패, CORS 차단 등)
 *
 * @why fetch는 이런 실패를 전부 밋밋한 `TypeError: Failed to fetch`로 던진다.
 * 그대로 위로 올리면 화면에는 원인을 알 수 없는 문구만 남으므로 여기서 이름을 붙인다.
 */
export class SheetsNetworkError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SheetsNetworkError'
  }
}

/**
 * 읽기 요청 타임아웃 (ms)
 *
 * @why 시트 전체 읽기는 행이 수천 건이어도 보통 1~3초면 끝난다.
 * 15초를 넘겼다면 느린 게 아니라 응답이 오지 않는 상태로 보는 편이 실제에 가깝다.
 */
export const READ_TIMEOUT_MS = 15_000

/**
 * 쓰기 요청 타임아웃 (ms)
 *
 * @why 쓰기는 실패 시 재시도가 위험(중복 저장)하므로 읽기보다 여유를 준다.
 * 그렇다고 무한정 기다리면 원래 버그로 돌아가므로 상한은 반드시 둔다.
 */
export const WRITE_TIMEOUT_MS = 25_000

/**
 * 읽기 재시도 횟수 (최초 시도 제외)
 *
 * @why 죽은 커넥션으로 나간 요청은 끊고 다시 보내면 새 커넥션이 열려 대개 성공한다.
 * 읽기는 멱등이라 재시도가 안전하다. 쓰기는 append가 비멱등이라 여기에 해당하지 않는다.
 */
const READ_RETRIES = 1

/**
 * 요청 단위 설정
 */
interface SheetsRequestConfig {
  /** 응답을 기다릴 최대 시간(ms) */
  timeoutMs?: number
  /** 최초 시도 실패 시 추가로 시도할 횟수. 멱등한 요청에만 1 이상을 준다 */
  retries?: number
  /** 실패 시 서버에 이미 반영됐을 수 있는 요청인지 (쓰기=true) */
  mutating?: boolean
}

/**
 * 끊고 다시 보내면 나아질 수 있는 실패인지 판정한다.
 *
 * @why 4xx(권한·인증·잘못된 범위)는 몇 번을 다시 보내도 같은 결과라
 * 재시도가 사용자 대기 시간만 늘린다. 일시적 네트워크 실패만 대상으로 한다.
 */
function isRetriableFailure(error: unknown): boolean {
  return error instanceof SheetsTimeoutError || error instanceof SheetsNetworkError
}

/**
 * Google Sheets API 응답 타입
 */
interface SheetsApiResponse {
  values?: string[][]
  error?: {
    code: number
    message: string
  }
}

/**
 * 시트 메타데이터 타입
 */
export interface SheetMetadata {
  sheetId: number
  title: string
  index: number
}

/**
 * 배치 쓰기 요청 타입
 */
export interface BatchWriteRequest {
  sheetName: string
  values: string[][]
}

/**
 * Google Sheets 클라이언트
 *
 * 스프레드시트 읽기/쓰기 기본 기능을 제공합니다.
 */
export class GoogleSheetsClient {
  private spreadsheetId: string
  private baseUrl: string

  constructor(spreadsheetId: string) {
    // Spreadsheet ID is now required (managed via workspace system)
    if (!spreadsheetId) {
      throw new Error(
        'Spreadsheet ID is required. Please configure a workspace first.\n' +
          'Go to Settings → Workspace Management to add a workspace.'
      )
    }

    this.spreadsheetId = spreadsheetId
    this.baseUrl = env.API_BASE_URL
  }

  /**
   * 인증 토큰 가져오기
   */
  private getAuthToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
  }

  /**
   * API 요청 1회 (타임아웃 포함)
   *
   * @why AbortController로 상한을 걸어야 하는 이유:
   * fetch는 응답이 오지 않아도 스스로 끊지 않는다. 끊어주지 않으면 호출측의
   * `await`이 영원히 반환되지 않고, `try/finally`의 정리 코드조차 실행되지 않아
   * 화면이 "저장 중" 상태로 굳는다(=이 파일이 고치는 원래 버그).
   *
   * 타이머 해제를 `finally`에 두는 이유: 응답 헤더뿐 아니라 본문(json) 수신까지
   * 타임아웃 대상에 포함해야 한다. body 스트림이 멈춘 채로 남는 경우도 같은 증상이다.
   */
  private async fetchOnce<T = unknown>(
    endpoint: string,
    options: RequestInit,
    timeoutMs: number,
    mutating: boolean
  ): Promise<T> {
    const token = this.getAuthToken()

    if (!token) {
      throw new Error('인증 토큰이 없습니다. 로그인이 필요합니다.')
    }

    const url = `${this.baseUrl}${endpoint}`
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...options.headers,
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorMessage = errorData.error?.message || response.statusText

        switch (response.status) {
          case 401:
            // 세션 만료 이벤트 발생 (전역 처리를 위해)
            window.dispatchEvent(new CustomEvent('session-expired'))
            throw new SheetsAuthenticationError(
              `인증 실패: ${errorMessage}. 다시 로그인해주세요.`
            )
          case 403:
            throw new SheetsPermissionError(
              `권한 없음: ${errorMessage}. Google Sheets 권한을 확인해주세요.`
            )
          case 404:
            throw new SheetsNotFoundError(
              `리소스 없음: ${errorMessage}. Spreadsheet ID를 확인해주세요.`
            )
          case 429:
            throw new SheetsRateLimitError(
              `요청 제한 초과: ${errorMessage}. 잠시 후 다시 시도해주세요.`
            )
          default:
            throw new Error(`API 오류 (${response.status}): ${errorMessage}`)
        }
      }

      return (await response.json()) as T
    } catch (error) {
      // abort는 우리가 건 타임아웃이다.
      // @why 던져진 값의 타입이 아니라 signal 상태로 판정하는 이유:
      // fetch가 던지는 AbortError는 DOMException인데, 이 타입이 Error를 상속하는지가
      // 실행 환경(브라우저 / jsdom)마다 다르다. signal.aborted는 우리가 건 타이머가
      // 실제로 끊었는지를 환경과 무관하게 알려주는 유일한 사실이다.
      if (controller.signal.aborted) {
        throw new SheetsTimeoutError(
          `서버 응답이 ${Math.round(timeoutMs / 1000)}초 안에 오지 않아 요청을 중단했습니다. ` +
            '네트워크 상태를 확인한 뒤 다시 시도해주세요.',
          timeoutMs,
          mutating
        )
      }

      // fetch는 오프라인·DNS 실패·CORS 차단을 모두 TypeError로 던진다
      if (error instanceof TypeError) {
        throw new SheetsNetworkError(
          `네트워크에 연결할 수 없습니다: ${error.message}`
        )
      }

      throw error
    } finally {
      clearTimeout(timer)
    }
  }

  /**
   * API 요청 (공통) — 타임아웃 + 선택적 재시도
   *
   * @why 재시도를 기본값 0으로 두는 이유: values.append는 비멱등이라
   * 같은 요청을 두 번 보내면 거래가 두 벌 쌓인다. 재시도는 멱등한 읽기만
   * 명시적으로 켜서 쓴다.
   */
  private async fetch<T = unknown>(
    endpoint: string,
    options: RequestInit = {},
    config: SheetsRequestConfig = {}
  ): Promise<T> {
    const {
      timeoutMs = READ_TIMEOUT_MS,
      retries = 0,
      mutating = false,
    } = config

    let lastError: unknown

    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        return await this.fetchOnce<T>(endpoint, options, timeoutMs, mutating)
      } catch (error) {
        lastError = error

        if (attempt === retries || !isRetriableFailure(error)) {
          throw error
        }

        logger.warn(
          `[GoogleSheetsClient] 요청 재시도 (${attempt + 1}/${retries}): ${endpoint}`,
          error
        )
      }
    }

    // 위 루프는 항상 return 또는 throw로 끝나지만, 타입상 도달 가능하므로 방어한다
    throw lastError
  }

  /**
   * 시트 데이터 읽기
   *
   * @param sheetName - 시트 이름 (예: "Transactions")
   * @param range - 읽을 범위 (예: "A:Z", "A1:Z100")
   * @returns 2차원 배열 (첫 행은 헤더)
   *
   * @throws {SheetsNotFoundError} 시트가 존재하지 않는 경우
   * @throws {SheetsAuthenticationError} 인증 실패
   * @throws {SheetsPermissionError} 권한 없음
   * @throws {SheetsRateLimitError} API 요청 제한 초과
   */
  async readSheet(
    sheetName: string,
    range = 'A:Z'
  ): Promise<string[][]> {
    try {
      const fullRange = `${sheetName}!${range}`
      const endpoint = `/spreadsheets/${this.spreadsheetId}/values/${encodeURIComponent(fullRange)}`

      // 읽기는 멱등하므로, 응답이 오지 않으면 끊고 새 커넥션으로 한 번 더 시도한다
      const response = await this.fetch<SheetsApiResponse>(
        endpoint,
        {},
        { timeoutMs: READ_TIMEOUT_MS, retries: READ_RETRIES }
      )

      return response.values || []
    } catch (error) {
      // 시트가 없는 경우 (400 에러 + "Unable to parse range" 메시지)
      if (error instanceof Error && error.message.includes('Unable to parse range')) {
        logger.warn(`⚠️ 시트가 존재하지 않습니다: ${sheetName}`)
        logger.warn('스프레드시트 초기화가 필요할 수 있습니다.')
        return []  // 빈 배열 반환 (에러 대신)
      }

      // 다른 에러는 그대로 던짐
      throw error
    }
  }

  /**
   * 시트에 데이터 추가 (행 추가)
   *
   * @param sheetName - 시트 이름
   * @param values - 추가할 데이터 (2차원 배열)
   */
  async appendSheet(
    sheetName: string,
    values: string[][]
  ): Promise<void> {
    logger.debug(`[GoogleSheetsClient] appendSheet 호출:`, {
      sheetName,
      values,
      rowCount: values.length
    })

    const range = `${sheetName}!A:Z`
    // valueInputOption은 URL 쿼리 파라미터로 전달
    const endpoint = `/spreadsheets/${this.spreadsheetId}/values/${encodeURIComponent(range)}:append?valueInputOption=RAW`

    // 쓰기는 비멱등(append)이라 자동 재시도하지 않는다.
    // 실패 시 실제 반영 여부 확인은 호출측(Repository)의 책임이다.
    const response = await this.fetch(
      endpoint,
      {
        method: 'POST',
        body: JSON.stringify({
          values,
        }),
      },
      { timeoutMs: WRITE_TIMEOUT_MS, retries: 0, mutating: true }
    )

    logger.debug(`[GoogleSheetsClient] appendSheet 응답:`, response)
  }

  /**
   * 시트 데이터 업데이트
   *
   * @param sheetName - 시트 이름
   * @param range - 업데이트할 범위 (예: "A2:Z2")
   * @param values - 업데이트할 데이터
   */
  async updateSheet(
    sheetName: string,
    range: string,
    values: string[][]
  ): Promise<void> {
    const fullRange = `${sheetName}!${range}`
    // valueInputOption은 URL 쿼리 파라미터로 전달
    const endpoint = `/spreadsheets/${this.spreadsheetId}/values/${encodeURIComponent(fullRange)}?valueInputOption=RAW`

    await this.fetch(
      endpoint,
      {
        method: 'PUT',
        body: JSON.stringify({
          values,
        }),
      },
      { timeoutMs: WRITE_TIMEOUT_MS, retries: 0, mutating: true }
    )
  }

  /**
   * 시트에서 행 삭제 (실제로는 빈 값으로 업데이트)
   *
   * Google Sheets API는 행 삭제가 복잡하므로
   * 일반적으로 isActive 필드를 false로 설정하여 비활성화합니다.
   */
  async deleteRow(
    sheetName: string,
    rowIndex: number
  ): Promise<void> {
    // 실제 구현에서는 해당 행을 찾아서 isActive를 false로 업데이트
    logger.warn(`[GoogleSheetsClient.deleteRow] 미구현 stub 호출됨: ${sheetName} 행 ${rowIndex}`)
  }

  /**
   * 새 스프레드시트 생성
   *
   * Static 메서드로 spreadsheetId 없이 호출 가능합니다.
   *
   * @param title - 스프레드시트 제목
   * @returns 생성된 spreadsheetId
   */
  static async createSpreadsheet(title: string): Promise<string> {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)

    if (!token) {
      throw new SheetsAuthenticationError('인증 토큰이 없습니다. 로그인이 필요합니다.')
    }

    const response = await fetch('https://sheets.googleapis.com/v4/spreadsheets', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        properties: {
          title,
          locale: 'ko_KR',
          timeZone: 'Asia/Seoul',
        },
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        `스프레드시트 생성 실패: ${errorData.error?.message || response.statusText}`
      )
    }

    const data = await response.json()
    return data.spreadsheetId
  }

  /**
   * 스프레드시트 메타데이터 조회 (시트 목록 포함)
   *
   * @returns 시트 정보 배열
   */
  async getSpreadsheetMetadata(): Promise<SheetMetadata[]> {
    const response = await this.fetch<any>(
      `/spreadsheets/${this.spreadsheetId}?fields=sheets.properties`,
      {},
      { timeoutMs: READ_TIMEOUT_MS, retries: READ_RETRIES }
    )

    return response.sheets.map((sheet: any) => ({
      sheetId: sheet.properties.sheetId,
      title: sheet.properties.title,
      index: sheet.properties.index,
    }))
  }

  /**
   * 시트 생성 (헤더 포함)
   *
   * @param sheetName - 생성할 시트 이름
   * @param headers - 헤더 행 (선택사항)
   * @returns 생성된 sheetId
   */
  async createSheet(sheetName: string, headers?: string[]): Promise<number> {
    // 1. 시트 생성 요청
    const createRequest = {
      addSheet: {
        properties: {
          title: sheetName,
          gridProperties: {
            frozenRowCount: 1, // 헤더 고정
          },
        },
      },
    }

    const response = await this.fetch<any>(
      `/spreadsheets/${this.spreadsheetId}:batchUpdate`,
      {
        method: 'POST',
        body: JSON.stringify({
          requests: [createRequest],
        }),
      }
    )

    const sheetId = response.replies[0].addSheet.properties.sheetId

    // 2. 헤더 작성 (있는 경우)
    if (headers && headers.length > 0) {
      await this.appendSheet(sheetName, [headers])
    }

    return sheetId
  }

  /**
   * 시트 이름 변경
   *
   * @param oldName - 기존 시트 이름
   * @param newName - 새 시트 이름
   */
  async renameSheet(oldName: string, newName: string): Promise<void> {
    // 1. 시트 ID 조회
    const sheets = await this.getSpreadsheetMetadata()
    const sheet = sheets.find((s) => s.title === oldName)

    if (!sheet) {
      throw new SheetsNotFoundError(`시트를 찾을 수 없습니다: ${oldName}`)
    }

    // 2. 이름 변경 요청
    await this.fetch(
      `/spreadsheets/${this.spreadsheetId}:batchUpdate`,
      {
        method: 'POST',
        body: JSON.stringify({
          requests: [
            {
              updateSheetProperties: {
                properties: {
                  sheetId: sheet.sheetId,
                  title: newName,
                },
                fields: 'title',
              },
            },
          ],
        }),
      }
    )
  }

  /**
   * 시트 삭제
   *
   * @param sheetName - 삭제할 시트 이름 (예: "Sheet1", "시트1")
   * @throws SheetsNotFoundError - 시트를 찾을 수 없는 경우
   */
  async deleteSheet(sheetName: string): Promise<void> {
    // 1. 시트 메타데이터 조회
    const sheets = await this.getSpreadsheetMetadata()
    const sheet = sheets.find((s) => s.title === sheetName)

    if (!sheet) {
      throw new SheetsNotFoundError(`시트를 찾을 수 없습니다: ${sheetName}`)
    }

    // 2. deleteSheet 요청
    await this.fetch(
      `/spreadsheets/${this.spreadsheetId}:batchUpdate`,
      {
        method: 'POST',
        body: JSON.stringify({
          requests: [
            {
              deleteSheet: {
                sheetId: sheet.sheetId,
              },
            },
          ],
        }),
      }
    )
  }

  /**
   * 시트를 첫 번째 위치로 이동
   *
   * @param sheetName - 이동할 시트 이름 (예: "README")
   * @throws SheetsNotFoundError - 시트를 찾을 수 없는 경우
   */
  async moveSheetToFirst(sheetName: string): Promise<void> {
    // 1. 시트 메타데이터 조회
    const sheets = await this.getSpreadsheetMetadata()
    const sheet = sheets.find((s) => s.title === sheetName)

    if (!sheet) {
      throw new SheetsNotFoundError(`시트를 찾을 수 없습니다: ${sheetName}`)
    }

    // 2. updateSheetProperties 요청 (index를 0으로 설정)
    await this.fetch(
      `/spreadsheets/${this.spreadsheetId}:batchUpdate`,
      {
        method: 'POST',
        body: JSON.stringify({
          requests: [
            {
              updateSheetProperties: {
                properties: {
                  sheetId: sheet.sheetId,
                  index: 0, // 첫 번째 위치
                },
                fields: 'index',
              },
            },
          ],
        }),
      }
    )
  }

  /**
   * 여러 시트에 데이터를 한 번에 쓰기 (배치)
   *
   * @param updates - 배치 업데이트 요청 배열
   */
  async batchWrite(updates: BatchWriteRequest[]): Promise<void> {
    const data = updates.map((update) => ({
      range: `${update.sheetName}!A:Z`,
      values: update.values,
    }))

    await this.fetch(
      `/spreadsheets/${this.spreadsheetId}/values:batchUpdate`,
      {
        method: 'POST',
        body: JSON.stringify({
          valueInputOption: 'RAW',
          data,
        }),
      },
      { timeoutMs: WRITE_TIMEOUT_MS, retries: 0, mutating: true }
    )
  }
}

/**
 * DEPRECATED: Default client instance removed.
 *
 * Use getWorkspaceClient() from '@/core/workspace/WorkspaceContext' instead.
 * This ensures the correct spreadsheetId from the active workspace is used.
 *
 * @example
 * ```typescript
 * import { getWorkspaceClient } from '@/core/workspace/WorkspaceContext'
 *
 * const client = getWorkspaceClient()
 * const rows = await client.readSheet('Transactions')
 * ```
 */
// export const googleSheetsClient = new GoogleSheetsClient() // REMOVED
